import React, { useState } from 'react';
import styled from 'styled-components';
import { Popover, Slider, Button, Typography } from '@mui/material';
import { panelTheme } from '../PanelComerciales.theme';
import { FlowTypeDef, sumSpec, flowTypeColor } from '../PanelComerciales.types';
import { SpecialtyBar } from '../PanelComerciales.styles';

interface SpecialtyEditorProps {
    value: Record<string, number>;
    flowTypes: FlowTypeDef[];
    onChange: (next: Record<string, number>) => void;
}

const PopoverBody = styled.div`
    width: 320px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;

    .row {
        display: flex;
        align-items: center;
        gap: 12px;

        .swatch {
            width: 8px;
            height: 8px;
            border-radius: 50%;
        }
        .label {
            min-width: 110px;
            font-size: 12px;
            color: ${panelTheme.colors.textMuted};
        }
        .pct {
            min-width: 36px;
            text-align: right;
            font-weight: 600;
            color: ${panelTheme.colors.text};
        }
    }

    .footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: 8px;
    }

    .total {
        font-size: 12px;
        font-weight: 600;
    }
    .total.invalid {
        color: ${panelTheme.colors.danger};
    }
    .total.valid {
        color: ${panelTheme.colors.success};
    }
`;

const SpecialtyEditor: React.FC<SpecialtyEditorProps> = ({ value, flowTypes, onChange }) => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [draft, setDraft] = useState<Record<string, number>>(value);
    const open = Boolean(anchor);

    const openEditor = (e: React.MouseEvent<HTMLElement>) => {
        setDraft(value);
        setAnchor(e.currentTarget);
    };
    const close = () => setAnchor(null);

    const total = sumSpec(draft);
    const valid = total === 100;

    const setKey = (k: string, v: number) =>
        setDraft((prev) => ({ ...prev, [k]: Math.max(0, Math.min(100, v)) }));

    const apply = () => {
        if (!valid) return;
        onChange(draft);
        close();
    };

    return (
        <>
            <span
                onClick={openEditor}
                style={{ cursor: 'pointer', display: 'inline-flex', gap: 6, alignItems: 'center' }}
            >
                <SpecialtyBar>
                    {flowTypes.map((ft, i) => {
                        const pct = value[ft.key] || 0;
                        if (pct <= 0) return null;
                        return (
                            <span
                                key={ft.key}
                                style={{ width: `${pct}%`, background: flowTypeColor(i) }}
                            />
                        );
                    })}
                </SpecialtyBar>
            </span>
            <Popover
                open={open}
                anchorEl={anchor}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: { borderRadius: panelTheme.radius.md },
                    },
                }}
            >
                <PopoverBody>
                    <Typography variant='caption' sx={{ color: panelTheme.colors.textMuted }}>
                        Distribución de especialidad (debe sumar 100%)
                    </Typography>
                    {flowTypes.map((ft, i) => (
                        <div className='row' key={ft.key}>
                            <span className='swatch' style={{ background: flowTypeColor(i) }} />
                            <span className='label'>{ft.label}</span>
                            <Slider
                                size='small'
                                value={draft[ft.key] ?? 0}
                                min={0}
                                max={100}
                                step={5}
                                onChange={(_, v) =>
                                    setKey(ft.key, Array.isArray(v) ? v[0] : v)
                                }
                                sx={{ color: flowTypeColor(i), flex: 1 }}
                            />
                            <span className='pct'>{draft[ft.key] ?? 0}%</span>
                        </div>
                    ))}
                    <div className='footer'>
                        <span className={`total ${valid ? 'valid' : 'invalid'}`}>
                            Total: {total}% {valid ? '' : '(debe ser 100)'}
                        </span>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Button size='small' onClick={close} sx={{ textTransform: 'none' }}>
                                Cancelar
                            </Button>
                            <Button
                                size='small'
                                variant='contained'
                                disabled={!valid}
                                onClick={apply}
                                sx={{
                                    textTransform: 'none',
                                    background: panelTheme.colors.indigo,
                                    '&:hover': { background: panelTheme.colors.violet },
                                }}
                            >
                                Aplicar
                            </Button>
                        </div>
                    </div>
                </PopoverBody>
            </Popover>
        </>
    );
};

export default SpecialtyEditor;
